import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';

const serviceSubLinks = [
  { label: 'Parsiyel Taşımacılık', href: '/hizmetlerimiz/parsiyel', icon: 'ri-box-3-line', desc: 'Küçük hacimli yükler' },
  { label: 'Komple Araç', href: '/hizmetlerimiz/komple-arac', icon: 'ri-truck-line', desc: 'Büyük hacimli yükler' },
  { label: 'Ev & Ofis Taşıma', href: '/hizmetlerimiz/ev-ofis-tasima', icon: 'ri-home-4-line', desc: 'Taşınma hizmetleri' },
  { label: 'Hızlı Kargo', href: '/hizmetlerimiz/hizli-kargo', icon: 'ri-flashlight-line', desc: 'Ekspres teslimat' },
];

const navLinks = [
  { label: 'Anasayfa', href: '/', anchor: '#home' },
  { label: 'Hizmetlerimiz', href: '/hizmetlerimiz', anchor: '#services', hasDropdown: true },
  { label: 'Neden Biz?', href: '/neden-biz' },
  { label: 'Referanslar', href: '/referanslar' },
  { label: 'İletişim', href: '/iletisim' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === '/';

  const openDropdown = useCallback(() => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    setDropdownOpen(true);
  }, []);

  const closeDropdown = useCallback(() => {
    closeTimerRef.current = setTimeout(() => setDropdownOpen(false), 120);
  }, []);

  useEffect(() => () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNavClick = (link: typeof navLinks[number]) => {
    setMenuOpen(false);
    setDropdownOpen(false);
    if (isHome && link.anchor && link.href === '/') {
      const el = document.querySelector(link.anchor);
      if (el) { el.scrollIntoView({ behavior: 'smooth' }); return; }
    }
    navigate(link.href);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white border-b border-gray-100' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 cursor-pointer">
            <img
              src="/media/54ce48c6-48b8-4db5-a1ba-60786d9cb830_TL-7ee2f101b8.png"
              alt="Töle Lojistik Logo"
              className="h-10 md:h-12 w-auto object-contain"
            />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6 lg:gap-8">
            {navLinks.map((link) =>
              link.hasDropdown ? (
                <div key={link.href} className="relative" ref={dropdownRef} onMouseEnter={openDropdown} onMouseLeave={closeDropdown}>
                  <button
                    onClick={() => setDropdownOpen((v) => !v)}
                    className={`flex items-center gap-1 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap bg-transparent border-none ${
                      scrolled ? 'text-brand-navy hover:text-brand-orange' : 'text-white hover:text-brand-orange'
                    }`}
                  >
                    {link.label}
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
                    </span>
                  </button>

                  {/* Dropdown */}
                  {dropdownOpen && (
                    <div
                      className="absolute top-full left-1/2 -translate-x-1/2 mt-1 w-64 bg-white rounded-2xl border border-gray-100 overflow-hidden"
                    >
                      {/* All Services link */}
                      <div className="px-4 pt-3 pb-2 border-b border-gray-50">
                        <Link
                          to="/hizmetlerimiz"
                          onClick={() => setDropdownOpen(false)}
                          className="flex items-center justify-between text-xs text-gray-400 hover:text-brand-orange transition-colors cursor-pointer font-medium"
                        >
                          Tüm Hizmetlerimiz
                          <span className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-arrow-right-line text-xs"></i>
                          </span>
                        </Link>
                      </div>
                      {serviceSubLinks.map((sub) => (
                        <Link
                          key={sub.href}
                          to={sub.href}
                          onClick={() => setDropdownOpen(false)}
                          className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer group"
                        >
                          <div className="w-8 h-8 flex items-center justify-center bg-brand-orange/10 text-brand-orange rounded-lg flex-shrink-0 group-hover:bg-brand-orange group-hover:text-white transition-colors">
                            <i className={`${sub.icon} text-sm`}></i>
                          </div>
                          <div>
                            <p className="text-brand-navy text-sm font-semibold leading-tight">{sub.label}</p>
                            <p className="text-gray-400 text-xs">{sub.desc}</p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <button
                  key={link.href}
                  onClick={() => handleNavClick(link)}
                  className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap bg-transparent border-none ${
                    scrolled ? 'text-brand-navy hover:text-brand-orange' : 'text-white hover:text-brand-orange'
                  }`}
                >
                  {link.label}
                </button>
              )
            )}
          </div>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://wa.me/905322376576"
              target="_blank"
              rel="nofollow noreferrer"
              className="flex items-center gap-2 bg-whatsapp hover:bg-whatsapp-dark text-white px-4 py-2 rounded-md text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-whatsapp-line text-base"></i>
              </span>
              WhatsApp
            </a>
            <a
              href="tel:+905322376576"
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap border ${
                scrolled
                  ? 'border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white'
                  : 'border-white/50 text-white hover:bg-white/10'
              }`}
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-phone-line text-base"></i>
              </span>
              +90 532 237 65 76
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className={`md:hidden flex items-center justify-center w-10 h-10 rounded-lg transition-colors ${
              scrolled ? 'text-brand-navy' : 'text-white'
            }`}
          >
            <i className={`text-2xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 px-4 py-4">
          <div className="flex flex-col gap-1">
            {navLinks.map((link) =>
              link.hasDropdown ? (
                <div key={link.href}>
                  <div className="flex items-center justify-between">
                    <button
                      onClick={() => { setMenuOpen(false); navigate(link.href); }}
                      className="text-brand-navy text-sm font-medium py-2 cursor-pointer text-left bg-transparent border-none"
                    >
                      {link.label}
                    </button>
                    <button
                      onClick={() => setMobileServicesOpen((v) => !v)}
                      className="p-2 text-brand-navy bg-transparent border-none cursor-pointer"
                    >
                      <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${mobileServicesOpen ? 'rotate-180' : ''}`}></i>
                    </button>
                  </div>
                  {mobileServicesOpen && (
                    <div className="bg-gray-50/70 rounded-xl mb-1 overflow-hidden">
                      {serviceSubLinks.map((sub) => (
                        <Link
                          key={sub.href}
                          to={sub.href}
                          onClick={() => setMenuOpen(false)}
                          className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-100 transition-colors cursor-pointer"
                        >
                          <div className="w-6 h-6 flex items-center justify-center bg-brand-orange/10 text-brand-orange rounded-md">
                            <i className={`${sub.icon} text-xs`}></i>
                          </div>
                          <span className="text-brand-navy text-sm font-medium">{sub.label}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <button
                  key={link.href}
                  onClick={() => handleNavClick(link)}
                  className="text-brand-navy text-sm font-medium py-2 border-b border-gray-50 last:border-0 cursor-pointer text-left bg-transparent border-l-0 border-r-0 border-t-0"
                >
                  {link.label}
                </button>
              )
            )}
            <div className="flex flex-col gap-2 pt-3">
              <a
                href="https://wa.me/905322376576"
                target="_blank"
                rel="nofollow noreferrer"
                className="flex items-center justify-center gap-2 bg-whatsapp text-white px-4 py-3 rounded-full text-sm font-semibold cursor-pointer"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-whatsapp-line"></i>
                </span>
                WhatsApp&apos;tan Yaz
              </a>
              <a
                href="tel:+905322376576"
                className="flex items-center justify-center gap-2 bg-brand-orange text-white px-4 py-3 rounded-full text-sm font-semibold cursor-pointer"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-phone-line"></i>
                </span>
                Hemen Ara
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
